import React from 'react';
import './CategoryPage.css';
import SearchBar from './SearchBar';
import SearchResults from './SearchResults';
import CollectionOverview from './CollectionOverview';
import CategoryIcon, { icons } from './CategoryIcon';

const HomePage = ({ config, searchQuery, setSearchQuery, searchResults, onSongClick, categories, onCategoryClick, categorySongCounts }) => {
  const totalSongs = categories
    ?.filter(cat => cat.id !== 'home')
    .reduce((total, cat) => total + (categorySongCounts[cat.id] || 0), 0) || 0;

  const totalCategories = categories?.filter(cat => cat.id !== 'home').length || 0;
  const largestCategory = Math.max(...Object.values(categorySongCounts || {}), 0);
  const categoriesWithSongs = Object.values(categorySongCounts || {}).filter(count => count > 0).length;

  return (
    <div className="home-page">
      {/* Floating Collection Overview button — always visible top-right */}
      <CollectionOverview
        totalSongs={totalSongs}
        totalCategories={totalCategories}
        largestCategory={largestCategory}
        categoriesWithSongs={categoriesWithSongs}
      />

      <div className="home-content">
        {/* Hero Section */}
        {!searchQuery && (
          <div className="hero-section">
            <div className="hero-background"></div>
            <div className="hero-content">
              <div className="hero-icon">
                <span>✝</span>
              </div>
              <h1 className="hero-title">
                {config?.app?.name || 'இதய ராகம்'}
              </h1>
              <p className="hero-subtitle">
                A Divine Collection of Christian Catholic Songs for Worship, Prayer, &amp; Spiritual Reflection
              </p>


              {/* Search Bar — right below title/tags */}
              <div className="home-search-wrap">
                <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
              </div>
            </div>
          </div>
        )}

        {/* Search Bar shown when hero is hidden (search active) */}
        {searchQuery && (
          <div className="home-search-wrap">
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </div>
        )}

        {/* Search Results */}
        <SearchResults searchResults={searchResults} onSongClick={onSongClick} />

        {/* Browse Categories */}
        {!searchQuery && (
          <div className="home-categories-section">
            <div className="categories-header">
              <h2 className="categories-title">Browse Categories</h2>
              <p className="categories-subtitle">Select a category to explore songs</p>
            </div>

            <div className="home-categories-grid">
              {categories?.filter(cat => cat.id !== 'home').map((category, index) => (
                <div
                  key={category.id}
                  className="home-category-card"
                  onClick={() => onCategoryClick(category.id)}
                  role="button"
                  tabIndex={0}
                  style={{ animationDelay: `${index * 0.06}s` }}
                  onKeyDown={(e) => e.key === 'Enter' && onCategoryClick(category.id)}
                >
                  {/* Shimmer overlay */}
                  <div className="hcc-shimmer" />

                  {/* Icon */}
                  <div className="hcc-icon-wrap">
                    {icons[category.id] ? (
                      <CategoryIcon categoryId={category.id} size={44} />
                    ) : category.icon?.startsWith('/') ? (
                      <img src={category.icon} alt={category.title} className="hcc-icon-img" />
                    ) : (
                      <span className="hcc-icon-emoji">{category.icon}</span>
                    )}
                  </div>

                  {/* Name */}
                  <h3 className="hcc-title">{category.title}</h3>

                  {/* Description */}
                  {category.description && (
                    <p className="hcc-desc">{category.description}</p>
                  )}

                  {/* Song count badge */}
                  {categorySongCounts[category.id] !== undefined && (
                    <div className="hcc-badge">
                      <span className="hcc-badge-dot" />
                      {categorySongCounts[category.id]} songs
                    </div>
                  )}

                  {/* Arrow */}
                  <div className="hcc-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        {!searchQuery && (
          <footer className="home-footer">
            <div className="footer-content">
              <div className="footer-grid">
                <div className="footer-section">
                  <div className="footer-brand">
                    <div className="footer-brand-icon">✝</div>
                    <h4>{config?.app?.name || 'இதய ராகம்'}</h4>
                  </div>
                  <p className="footer-text">A sacred collection of Christian Catholic songs for worship and spiritual reflection.</p>
                </div>
                <div className="footer-section">
                  <h4>Quick Links</h4>
                  <ul className="footer-links">
                    <li onClick={() => onCategoryClick('home')}>• Home</li>
                    {categories?.filter(cat => cat.id !== 'home').slice(0, 4).map(cat => (
                      <li key={cat.id} onClick={() => onCategoryClick(cat.id)}>• {cat.title}</li>
                    ))}
                  </ul>
                </div>
                <div className="footer-section">
                  <h4>Connect</h4>
                  <p className="footer-text">Share the joy of sacred music with others</p>
                  <div className="footer-social">
                    <div className="social-icon" title="Email">📧</div>
                    <div className="social-icon" title="Share">🔗</div>
                    <div className="social-icon" title="WhatsApp">💬</div>
                  </div>
                </div>
              </div>
              <div className="footer-bottom">
                <p className="footer-copyright">© 2026 {config?.app?.name || 'Idhaya Ragam'} — Christian Songs Collection</p>
                <p className="footer-note">Made with ❤️ for worship &amp; prayer</p>
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default HomePage;
